import {defineMessages} from "@project/i18n";

export const componentMessages = defineMessages({
  tablePaginationPageSize: {
    id: "components.table.pagination.pageSize",
    defaultMessage: "{pageSize} / page",
    description: "Page size option label in reusable table pagination component",
  },
  tablePaginationSummary: {
    id: "components.table.pagination.summary",
    defaultMessage: "{startItem} to {endItem} of {totalItems} results",
    description: "Result range summary in reusable table pagination component",
  },
  tablePaginationPrevious: {
    id: "components.table.pagination.previous",
    defaultMessage: "Prev",
    description: "Previous page button label in reusable table pagination component",
  },
  tablePaginationNext: {
    id: "components.table.pagination.next",
    defaultMessage: "Next",
    description: "Next page button label in reusable table pagination component",
  },
  tableSearchLabel: {
    id: "components.table.search.label",
    defaultMessage: "Search",
    description: "Default accessible label for reusable table search field",
  },
  tableSearchPlaceholder: {
    id: "components.table.search.placeholder",
    defaultMessage: "Search…",
    description: "Default placeholder for reusable table search field",
  },
  tableEmptyStateNoResults: {
    id: "components.table.emptyState.noResults",
    defaultMessage: "No results found",
    description: "Default empty-state message for reusable table empty state component",
  },
  formReset: {
    id: "components.form.reset",
    defaultMessage: "Reset",
    description: "Reusable reset button label in form footer components",
  },
  formCreate: {
    id: "components.form.create",
    defaultMessage: "Create",
    description: "Default create submit button label in reusable create-form footer",
  },
  formSave: {
    id: "components.form.save",
    defaultMessage: "Save",
    description: "Default save submit button label in reusable edit-form footer",
  },
  multiStepFormBack: {
    id: "components.form.multiStep.back",
    defaultMessage: "Back",
    description: "Back button label in reusable multi-step form footer",
  },
  multiStepFormContinue: {
    id: "components.form.multiStep.continue",
    defaultMessage: "Continue",
    description: "Continue button label in reusable multi-step form footer",
  },
  multiStepFormSkip: {
    id: "components.form.multiStep.skip",
    defaultMessage: "Skip",
    description: "Skip button label for optional multi-step form steps",
  },
  multiStepFormReview: {
    id: "components.form.multiStep.review",
    defaultMessage: "Review",
    description: "Review button label before reaching reusable multi-step form review step",
  },
  multiStepFormOptional: {
    id: "components.form.multiStep.optional",
    defaultMessage: "Optional",
    description: "Subtle label marking optional steps in reusable multi-step form stepper",
  },
  formFilterResetAria: {
    id: "components.form.filter.reset.aria",
    defaultMessage: "Reset filter selections to defaults",
    description: "Accessible label for reset button in reusable filter footer",
  },
  formFilterClear: {
    id: "components.form.filter.clear",
    defaultMessage: "Clear",
    description: "Label for clear button in reusable filter footer",
  },
  formFilterClearAria: {
    id: "components.form.filter.clear.aria",
    defaultMessage: "Clear all active filters",
    description: "Accessible label for clear button in reusable filter footer",
  },
  formFilterApply: {
    id: "components.form.filter.apply",
    defaultMessage: "Apply",
    description: "Label for apply button in reusable filter footer",
  },
  formFilterApplyAria: {
    id: "components.form.filter.apply.aria",
    defaultMessage: "Apply current filter selections",
    description: "Accessible label for apply button in reusable filter footer",
  },
  confirmDeleteTitle: {
    id: "components.dialog.confirmDelete.title",
    defaultMessage: "Delete permanently?",
    description: "Default title for reusable confirm delete dialog",
  },
  confirmDeleteConfirm: {
    id: "components.dialog.confirmDelete.confirm",
    defaultMessage: "Delete",
    description: "Default confirm button label for reusable confirm delete dialog",
  },
  confirmDeleteCancel: {
    id: "components.dialog.confirmDelete.cancel",
    defaultMessage: "Cancel",
    description: "Cancel button label for reusable confirm delete dialog",
  },
  confirmDeleteDescriptionWithItemPrefix: {
    id: "components.dialog.confirmDelete.description.item.prefix",
    defaultMessage: "This will permanently delete",
    description:
      "Prefix of delete warning when dialog knows item name and item name is emphasized separately",
  },
  confirmDeleteDescriptionWithItemSuffix: {
    id: "components.dialog.confirmDelete.description.item.suffix",
    defaultMessage: "This action cannot be undone.",
    description:
      "Suffix of delete warning when dialog knows item name and item name is emphasized separately",
  },
  confirmDeleteDescriptionGeneric: {
    id: "components.dialog.confirmDelete.description.generic",
    defaultMessage: "This will permanently delete this item. This action cannot be undone.",
    description: "Default delete warning when dialog has no specific item name",
  },
  confirmSoftDeleteTitle: {
    id: "components.dialog.confirmSoftDelete.title",
    defaultMessage: "Delete this item?",
    description: "Default title for reusable reversible delete dialog",
  },
  confirmSoftDeleteDescriptionWithItemPrefix: {
    id: "components.dialog.confirmSoftDelete.description.item.prefix",
    defaultMessage: "This will delete",
    description:
      "Prefix of reversible delete warning when dialog knows item name and item name is emphasized separately",
  },
  confirmSoftDeleteDescriptionWithItemSuffix: {
    id: "components.dialog.confirmSoftDelete.description.item.suffix",
    defaultMessage: "You can restore it later.",
    description: "Suffix explaining that a deleted item can be restored",
  },
  confirmSoftDeleteDescriptionGeneric: {
    id: "components.dialog.confirmSoftDelete.description.generic",
    defaultMessage: "This item will be deleted. You can restore it later.",
    description: "Default reversible delete warning when dialog has no specific item name",
  },
  mermaidWaitingForValidDiagram: {
    id: "components.visualization.mermaid.waiting",
    defaultMessage: "Waiting for valid diagram data...",
    description: "Fallback message while Mermaid diagram source is still invalid during rendering",
  },
  mermaidActions: {
    id: "components.visualization.mermaid.actions",
    defaultMessage: "Diagram actions",
    description: "Toolbar accessible label for Mermaid diagram controls",
  },
  mermaidCopyCode: {
    id: "components.visualization.mermaid.copyCode",
    defaultMessage: "Copy code",
    description: "Tooltip label for Mermaid diagram copy button",
  },
  mermaidResetZoom: {
    id: "components.visualization.mermaid.resetZoom",
    defaultMessage: "Reset zoom",
    description: "Tooltip label for Mermaid diagram reset zoom button",
  },
  overviewActions: {
    id: "components.overview.actions",
    defaultMessage: "Actions",
    description: "Default accessible label for overview panel actions menu trigger",
  },
  unknownDate: {
    id: "components.date.unknown",
    defaultMessage: "Unknown date",
    description:
      "Accessible label shown when reusable date display component has invalid date value",
  },
  copyId: {
    id: "components.copy.id",
    defaultMessage: "Copy ID",
    description: "Tooltip label for reusable copy-id component",
  },
  copied: {
    id: "components.copy.copied",
    defaultMessage: "Copied!",
    description: "Tooltip label shown after reusable copy-id action succeeds",
  },
});
